import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FlaskConical, RotateCcw } from "lucide-react";

const gbp = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const truthy = (v) => v === true || ["1", "true", "yes", "y", "on", "selected"].includes(String(v).toLowerCase());
function componentVisible(comp, answers) {
  const c = comp.condition;
  if (!c || !c.conditions || !c.conditions.length) return true;
  const one = (cn) => {
    const v = answers[cn.factor];
    switch (cn.op) {
      case "yes": case "true": return truthy(v);
      case "no": case "false": return !truthy(v);
      case "eq": return String(v).toLowerCase() === String(cn.value).toLowerCase();
      case "ne": return String(v).toLowerCase() !== String(cn.value).toLowerCase();
      case "gt": return Number(v) > Number(cn.value);
      case "lt": return Number(v) < Number(cn.value);
      default: return true;
    }
  };
  const res = c.conditions.map(one);
  return c.match === "any" ? res.some(Boolean) : res.every(Boolean);
}

// Normalise: include DRAFT components (staged) in the test; keep inactive ones out.
function normaliseComponents(components) {
  return (components || [])
    .filter((c) => c.active !== false)
    .map((c) => (c.status === "draft" ? { ...c, status: undefined } : c));
}

function defaultFor(c) {
  if (c.input_type === "yes_no") return c.default === true || ["yes", "true", "1"].includes(String(c.default).toLowerCase());
  if (c.input_type === "dropdown") {
    const m = (c.options || []).find((o) => [o.key, o.name].map((x) => String(x).toLowerCase()).includes(String(c.default).toLowerCase()));
    return m ? (m.key || m.name) : "";
  }
  if (c.input_type === "service_ref") return "all";
  return (c.default !== undefined && c.default !== null && c.default !== "" && !isNaN(Number(c.default))) ? Number(c.default) : "";
}

export default function PricingTester({ serviceName = "", components = [], focusKey = null, onClose }) {
  const norm = useMemo(() => normaliseComponents(components), [components]);
  const inputs = useMemo(
    () => norm.filter((c) => !["service_selection", "automatic"].includes(c.input_type))
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999)),
    [norm]
  );

  const buildDefaults = () => {
    const a = {};
    inputs.forEach((c) => { a[c.key] = defaultFor(c); });
    return a;
  };
  const [answers, setAnswers] = useState(buildDefaults);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const focusComp = focusKey ? norm.find((c) => c.key === focusKey) : null;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/pricing/preview", { pricing_v2: { components: norm }, answers });
        if (alive) setResult(data);
      } catch { if (alive) setResult(null); }
      finally { if (alive) setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [answers, norm]);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const reset = () => setAnswers(buildDefaults());

  const visibleInputs = inputs.filter((c) => componentVisible(c, answers));
  const lines = result?.lines || [];
  const monthlyLines = lines.filter((l) => l.period === "monthly");
  const oneOffLines = lines.filter((l) => l.period === "one_off");
  const focusLine = focusKey ? lines.find((l) => l.key === focusKey) : null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pricing-tester">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-cass-teal" />
            {focusComp ? `Test component — ${focusComp.label || focusComp.name || focusComp.key}` : `Test service pricing — ${serviceName}`}
          </DialogTitle>
        </DialogHeader>

        {focusComp && (
          <Card className="p-3 bg-cass-teal/5 border-cass-teal/40" data-testid="tester-focus-summary">
            <p className="text-xs text-cass-slate">This component's contribution with the current sample answers</p>
            <p className="text-2xl font-bold text-cass-navy" data-testid="tester-focus-amount">
              {focusLine ? gbp(focusLine.amount) : gbp(0)}
              <span className="text-sm font-normal text-cass-slate ml-2">{focusLine ? (focusLine.period === "one_off" ? "one-off" : "/month") : "with current answers"}</span>
            </p>
            <p className="text-[11px] text-cass-slate mt-1">Contributions can depend on other answers (multipliers, dependencies), so all questions are shown below.</p>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          {/* Inputs */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-cass-slate/70">Sample answers</h4>
              <Button size="sm" variant="ghost" onClick={reset} data-testid="tester-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset</Button>
            </div>
            {visibleInputs.length === 0 && <p className="text-sm text-cass-slate">No answerable questions — this service prices automatically.</p>}
            {visibleInputs.map((c) => {
              const highlight = focusKey && c.key === focusKey;
              return (
                <div key={c.key} className={`rounded-md ${highlight ? "ring-1 ring-cass-teal p-2 bg-cass-teal/5" : ""}`} data-testid={`tester-field-wrap-${c.key}`}>
                  <Label className="text-xs">{c.label || c.name || c.key}{highlight && <span className="text-cass-teal ml-1">(testing)</span>}</Label>
                  {c.input_type === "yes_no" && (
                    <div className="flex items-center gap-2 h-9">
                      <Switch checked={truthy(answers[c.key])} onCheckedChange={(v) => set(c.key, !!v)} data-testid={`tester-field-${c.key}`} />
                      <span className="text-sm text-cass-slate">{truthy(answers[c.key]) ? "Yes" : "No"}</span>
                    </div>
                  )}
                  {c.input_type === "dropdown" && (
                    <Select value={String(answers[c.key] ?? "")} onValueChange={(v) => set(c.key, v)}>
                      <SelectTrigger data-testid={`tester-field-${c.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{(c.options || []).filter((o) => o.active !== false).map((o) => <SelectItem key={o.key || o.name} value={o.key || o.name}>{o.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {c.input_type === "service_ref" && (
                    <Select value={String(answers[c.key] ?? "all")} onValueChange={(v) => set(c.key, v)}>
                      <SelectTrigger data-testid={`tester-field-${c.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="all">All recurring services</SelectItem></SelectContent>
                    </Select>
                  )}
                  {(c.input_type === "number" || c.input_type === "currency") && (
                    <Input type="number" value={answers[c.key] ?? ""} onChange={(e) => set(c.key, e.target.value)} data-testid={`tester-field-${c.key}`} />
                  )}
                  {c.help_text && <p className="text-[11px] text-cass-slate mt-1">{c.help_text}</p>}
                </div>
              );
            })}
          </div>

          {/* Results */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-cass-slate/70">Calculated fee {loading && <span className="text-cass-teal normal-case">· updating…</span>}</h4>
            <Card className="p-3" data-testid="tester-results">
              {lines.length === 0 && <p className="text-sm text-cass-slate">No fee with the current answers.</p>}
              {monthlyLines.length > 0 && (
                <div className="mb-2">
                  <p className="text-[11px] font-semibold text-cass-slate/70 uppercase">Monthly</p>
                  {monthlyLines.map((l, i) => (
                    <div key={i} className={`flex justify-between text-sm py-0.5 ${focusKey && l.key === focusKey ? "text-cass-teal font-semibold" : ""}`} data-testid={`tester-line-${l.key}`}>
                      <span className="truncate mr-2">{l.label}</span><span>{gbp(l.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              {oneOffLines.length > 0 && (
                <div className="mb-2">
                  <p className="text-[11px] font-semibold text-cass-slate/70 uppercase">One-off</p>
                  {oneOffLines.map((l, i) => (
                    <div key={i} className={`flex justify-between text-sm py-0.5 ${focusKey && l.key === focusKey ? "text-cass-teal font-semibold" : ""}`} data-testid={`tester-line-oneoff-${l.key}`}>
                      <span className="truncate mr-2">{l.label}</span><span>{gbp(l.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-3 bg-cass-navy text-white" data-testid="tester-totals">
              <div className="flex justify-between text-sm py-0.5"><span>Monthly recurring (net)</span><span data-testid="tester-monthly">{gbp(result?.monthly)}</span></div>
              <div className="flex justify-between text-sm py-0.5"><span>One-off (net)</span><span data-testid="tester-oneoff">{gbp(result?.one_off)}</span></div>
              <div className="border-t border-white/20 my-1" />
              <div className="flex justify-between text-xs py-0.5 text-white/80"><span>VAT (on recommended)</span><span>{gbp(result?.vat)}</span></div>
              <div className="flex justify-between text-sm py-0.5 font-semibold"><span>Gross (recommended)</span><span data-testid="tester-gross">{gbp(result?.gross)}</span></div>
              {result?.minimum_applied && <Badge className="mt-2 bg-cass-bronze text-[10px]">Minimum fee applied</Badge>}
            </Card>
            <p className="text-[11px] text-cass-slate">Draft components are included here so you can test staged pricing before activating. Live proposals ignore drafts.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
